import { useEffect, useRef } from "react";
import { WORLD_DOTS } from "./worldDots";

const LAT_TOP = 82;
const LAT_BOTTOM = -58;

export const WorldMap = ({ className = "", pulse = null, extraPulse = null }) => {
  const canvasRef = useRef(null);
  const staticRef = useRef(null);
  const pulsesRef = useRef([]);
  const ambientRef = useRef({ lastSpawn: 0, items: [] });
  const pulseKeyRef = useRef(null);
  const extraKeyRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let raf;
    let w = 0;
    let h = 0;
    let dpr = 1;
    const mouse = { x: 0, y: 0, tx: 0, ty: 0 };

    const proj = (lon, lat, mw, mh) => [
      ((lon + 180) / 360) * mw,
      ((LAT_TOP - lat) / (LAT_TOP - LAT_BOTTOM)) * mh,
    ];

    const buildStatic = () => {
      const layer = document.createElement("canvas");
      layer.width = w * dpr;
      layer.height = h * dpr;
      const c = layer.getContext("2d");
      c.scale(dpr, dpr);
      for (const [lon, lat] of WORLD_DOTS) {
        const [x, y] = proj(lon, lat, w, h);
        const r = Math.random();
        c.fillStyle =
          r < 0.13
            ? "rgba(16,185,129,0.5)"
            : r < 0.52
              ? "rgba(59,130,246,0.48)"
              : "rgba(0,240,255,0.5)";
        const s = r < 0.05 ? 2.7 : 1.9;
        c.fillRect(x - s / 2, y - s / 2, s, s);
      }
      staticRef.current = layer;
    };

    const draw = (t) => {
      ctx.clearRect(0, 0, w, h);
      mouse.x += (mouse.tx - mouse.x) * 0.05;
      mouse.y += (mouse.ty - mouse.y) * 0.05;
      if (staticRef.current) {
        ctx.globalAlpha = 0.7 + 0.12 * Math.sin(t / 1900);
        ctx.drawImage(staticRef.current, mouse.x * 16, mouse.y * 12, w, h);
        ctx.globalAlpha = 1;
      }
      const now = performance.now();
      if (!reduce && now - ambientRef.current.lastSpawn > 520) {
        ambientRef.current.lastSpawn = now;
        const spot = WORLD_DOTS[Math.floor(Math.random() * WORLD_DOTS.length)];
        const r = Math.random();
        ambientRef.current.items.push({
          x0: spot[0],
          y0: spot[1],
          t0: now,
          life: 1900 + Math.random() * 900,
          hue: r < 0.14 ? "16,185,129" : r < 0.55 ? "59,130,246" : "0,240,255",
        });
        if (ambientRef.current.items.length > 14) ambientRef.current.items.shift();
      }
      ambientRef.current.items = ambientRef.current.items.filter((p) => now - p.t0 < p.life);
      for (const p of ambientRef.current.items) {
        const k = (now - p.t0) / p.life;
        const [x, y] = proj(p.x0, p.y0, w, h);
        ctx.beginPath();
        ctx.arc(x, y, 2 + k * 16, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(${p.hue},${(1 - k) * 0.32})`;
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(x, y, 1.8, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.hue},${(1 - k) * 0.75})`;
        ctx.fill();
      }
      pulsesRef.current = pulsesRef.current.filter((p) => now - p.t0 < 2600);
      for (const p of pulsesRef.current) {
        const k = (now - p.t0) / 2600;
        const [x, y] = proj(p.lon, p.lat, w, h);
        ctx.beginPath();
        ctx.arc(x, y, 6 + k * 48, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(255,159,28,${(1 - k) * 0.5})`;
        ctx.lineWidth = 1.6;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(x, y, 3.4, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,190,80,${(1 - k) * 0.9})`;
        ctx.fill();
      }
    };

    const loop = (t) => {
      draw(t);
      raf = requestAnimationFrame(loop);
    };

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.offsetWidth;
      h = canvas.offsetHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      buildStatic();
    };

    const onMouse = (e) => {
      mouse.tx = e.clientX / window.innerWidth - 0.5;
      mouse.ty = e.clientY / window.innerHeight - 0.5;
    };

    resize();
    window.addEventListener("resize", resize);
    if (reduce) {
      draw(performance.now());
    } else {
      window.addEventListener("mousemove", onMouse);
      raf = requestAnimationFrame(loop);
    }
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMouse);
    };
  }, []);

  useEffect(() => {
    if (pulse && pulse.key !== pulseKeyRef.current) {
      pulseKeyRef.current = pulse.key;
      pulsesRef.current = pulsesRef.current.concat({
        lon: pulse.lon,
        lat: pulse.lat,
        t0: performance.now(),
      });
      if (pulsesRef.current.length > 6) {
        pulsesRef.current = pulsesRef.current.slice(-6);
      }
    }
  }, [pulse]);

  useEffect(() => {
    if (extraPulse && extraPulse.key !== extraKeyRef.current) {
      extraKeyRef.current = extraPulse.key;
      pulsesRef.current = pulsesRef.current.concat({
        lon: extraPulse.lon,
        lat: extraPulse.lat,
        t0: performance.now(),
      });
      if (pulsesRef.current.length > 6) {
        pulsesRef.current = pulsesRef.current.slice(-6);
      }
    }
  }, [extraPulse]);

  return <canvas ref={canvasRef} className={className} aria-hidden="true" />;
};

export default WorldMap;